// src/app/shared/services/edit/query-edit.service.ts

import { Injectable, inject } from '@angular/core'
import { firstValueFrom, Observable, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import { GeneratedContent, Hunt } from '@cyborg-monorepo/hunter/common'

import { PivotAPIService } from '../api/pivot-api.service'
import { NavigationService } from '../navigation.service'
import { QueryIndexService } from '../query-index.service'
import { SessionService } from '../session.service'
import { UndoRedoService } from '../undo-redo.service'
import { SelectedQueryPath } from '../utils/navigation.utils'

@Injectable({ providedIn: 'root' })
export class QueryEditorService {
  private readonly session = inject(SessionService)
  private readonly nav = inject(NavigationService)
  private readonly queryIndex = inject(QueryIndexService)
  private readonly undoRedo = inject(UndoRedoService<Hunt>)
  private readonly pivotApi = inject(PivotAPIService)

  /** Update the currently selected query or pivot */
  async updateQueryContent(
    patch: Partial<GeneratedContent>,
    path: SelectedQueryPath | null = this.nav.parsedSelectedQuery(),
  ): Promise<void> {
    const hunt = this.session.activeHunt()
    if (!hunt || !path) return

    this.undoRedo.push(hunt)
    const original = this.queryIndex.select(hunt, path)
    if (!original?.id) return

    // apply the patch with our new helper
    const updatedHunt = this.queryIndex.updateGeneratedContent(
      hunt,
      original.id,
      (old) => ({ ...old, ...patch }),
    )

    // clear cached query index so subsequent lookups see the new data
    this.queryIndex.clearCache(hunt.id)

    // persist & re-emit
    this.session.activeHunt.set(updatedHunt)
  }

  /** Append new pivots and navigate into them */
  async appendPivots(newPivots: GeneratedContent[]): Promise<void> {
    const hunt = this.session.activeHunt()
    const path = this.nav.parsedSelectedQuery()
    if (!hunt || !path) return

    this.undoRedo.push(hunt)
    const original = this.queryIndex.select(hunt, path)
    if (!original || !original.id) return

    const merged: GeneratedContent[] = [
      ...(original.pivots ?? []),
      ...newPivots,
    ]
    await this.updateQueryContent({ pivots: merged })

    newPivots.forEach((pivot) => {
      if (pivot.id) this.nav.pushPivot(pivot.id)
    })
    this.queryIndex.clearCache(hunt.id)
  }

  /** Generate pivot content then append */
  async generateContentForPivot(
    pivotId: string,
    toolId: string,
    pivotFields: Record<string, string>,
  ): Promise<void> {
    const generated = await firstValueFrom(
      this.pivotApi.getContentForPivot(pivotId, toolId, pivotFields),
    )
    await this.appendPivots(generated)
  }

  private replaceInTree(
    tree: GeneratedContent[],
    id: string,
    newNode: GeneratedContent,
  ): GeneratedContent[] {
    return tree.map((node) => {
      if (node.id === id) return newNode
      if (node.pivots?.length) {
        return { ...node, pivots: this.replaceInTree(node.pivots, id, newNode) }
      }
      return node
    })
  }

  /** Wire up live form inputs to auto-patch with debounce */
  public sync(
    raw$: Observable<string>,
    notes$: Observable<string>,
    path: SelectedQueryPath,
  ): Subscription {
    const sub = new Subscription()

    sub.add(
      raw$
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((q) => this.updateQueryContent({ query: q }, path)),
    )

    sub.add(
      notes$
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((n) => this.updateQueryContent({ notes: n }, path)),
    )

    return sub
  }
}
